import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef } from 'react';

import type { ColorType } from '@lib/types';
import { cn, cva, type VariantProps, setThisClass } from '@lib/utils';

const badgeVariants = cva(
  [
    // Layout
    'inline-flex items-center justify-center',
    // Typography
    'text-xs font-medium leading-none tracking-wide',
  ],
  {
    variants: {
      variant: {
        filled: [
          'bg-this-solid', // Background
          'text-white', // Text
        ],
        outlined: [
          'border border-this-line', // Border
          'text-this-text', // Text
        ],
        soft: [
          'bg-this-bg', // Background
          'text-this-text', // Text
        ],
        'soft-outlined': [
          'bg-this-bg', // Background
          'border border-this-line', // Border
          'text-this-text', // Text
        ],
      },
      size: {
        md: 'px-2 py-1',
        sm: 'h-4 min-w-4 px-1 py-0',
      },
      rounded: {
        default: 'rounded-sm',
        full: 'rounded-full',
      },
      glow: {
        true: 'shadow-lg shadow-this-solid/50',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'filled',
      size: 'md',
      rounded: 'default',
      glow: false,
    },
  }
);

type BadgeProps = {
  color?: ColorType;
} & VariantProps<typeof badgeVariants> &
  ComponentPropsWithoutRef<'span'>;

const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, size, rounded, color = 'neutral', glow, children, ...rest }, ref) => (
    <span
      ref={ref}
      className={cn(
        badgeVariants({ variant, size, rounded, glow }),
        setThisClass(color),
        className
      )}
      {...rest}
    >
      {children}
    </span>
  )
);

Badge.displayName = 'Badge';

export { Badge };
export type { BadgeProps };
